/**
 * HelpTopic — single help-section page rendered from helpContent.js
 *
 * Lives at /help/:slug. Behind PrivateRoute. The same body content is
 * also pushed into the per-page contextual <HelpButton> sheet — keeping
 * a single source of truth in helpContent.js.
 */
import { useParams, Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { getHelpTopic, HELP_TOPICS } from "@/lib/helpContent";

export default function HelpTopic() {
  const { slug } = useParams();
  const topic = getHelpTopic(slug);

  if (!topic) {
    return (
      <div className="animate-fade-in">
        <PageHeader title="Help" subtitle="Topic not found" />
        <div className="surface p-8 text-center text-charcoal-soft" data-testid="help-topic-404">
          We couldn&apos;t find a help topic for &ldquo;{slug}&rdquo;.{" "}
          <Link to="/help" className="text-ochre-600 underline">Back to all help</Link>.
        </div>
      </div>
    );
  }

  const Icon = topic.icon;
  // Suggest a few sibling topics so users keep reading instead of
  // bouncing back to the hub every time.
  const related = HELP_TOPICS.filter((t) => t.slug !== slug).slice(0, 4);

  return (
    <div className="animate-fade-in" data-testid={`help-topic-${slug}`}>
      <PageHeader
        title={topic.title}
        subtitle={topic.blurb}
        action={
          <Link to="/help">
            <Button variant="outline" className="h-10 gap-2 border-eucalyptus-700 text-eucalyptus-900">
              <ArrowLeft className="w-4 h-4" /> All help
            </Button>
          </Link>
        }
      />

      <div className="grid lg:grid-cols-3 gap-6">
        <article className="surface p-6 lg:col-span-2 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-md bg-eucalyptus-100 text-eucalyptus-900 grid place-items-center">
              <Icon className="w-6 h-6" />
            </div>
            <div className="label-eyebrow">HACCP record</div>
          </div>
          <HelpBody body={topic.body} />
        </article>

        <aside className="space-y-4">
          <div className="surface p-5">
            <div className="label-eyebrow mb-3">Related topics</div>
            <ul className="space-y-2">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`/help/${r.slug}`}
                    className="text-sm hover:text-eucalyptus-900 underline-offset-4 hover:underline"
                    data-testid={`help-related-${r.slug}`}
                  >
                    {r.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="surface-alt p-5 text-xs text-charcoal-soft">
            Still stuck? <a className="underline text-eucalyptus-900 font-medium" href="mailto:support@haccp.au">support@haccp.au</a>
          </div>
        </aside>
      </div>
    </div>
  );
}

export function HelpBody({ body }) {
  return (
    <div className="prose prose-sm max-w-none">
      {(body || []).map((block, i) => {
        if (block.type === "h") {
          return (
            <h3 key={i} className="font-display font-bold text-lg mt-5 first:mt-0">
              {block.text}
            </h3>
          );
        }
        if (block.type === "ol") {
          return (
            <ol key={i} className="list-decimal pl-6 space-y-1.5 text-sm">
              {(block.items || []).map((it, j) => (<li key={j}>{it}</li>))}
            </ol>
          );
        }
        return (
          <p key={i} className="text-sm leading-relaxed text-charcoal">{block.text}</p>
        );
      })}
    </div>
  );
}
